import { roleCanAccess } from "@l30/shared";
import { useIdeationMapNodes, useIdeationMaps } from "@l30/web-atlas/api/ideation-maps";
import { useDocuments } from "@l30/web-documents/api/documents";
import { useEffect, useMemo, useState } from "react";

import {
  filterNavSectionsForFlags,
  filterNavSectionsForRole,
} from "../components/layout/nav-config.js";
import {
  buildInsightsSearchResults,
  groupInsightsResults,
  type InsightsSearchBundle,
  type InsightsSearchGroupView,
  type InsightsSearchResult,
} from "../lib/insights-search.js";

import { useCurrentRole } from "./auth.js";
import { useCompanies } from "./companies.js";
import { useContacts } from "./contacts.js";
import { useFeatureFlags } from "./feature-flags.js";
import { useMeetingRecordings } from "./meetings.js";
import { useNotes } from "./notes.js";
import { useProjects } from "./projects.js";
import { useWorkspaces, useWorkspaceDocumentsIndex } from "./workspaces.js";

const SERVER_MIN = 2;
const DEBOUNCE_MS = 200;

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function assembleInsightsSearchBundle(input: {
  enabled: boolean;
  serverOk: boolean;
  canIdeation: boolean;
  canDocuments: boolean;
  canProjects: boolean;
  canCompanies: boolean;
  canContacts: boolean;
  canNotes: boolean;
  canMeetings: boolean;
  nodes: InsightsSearchBundle["nodes"];
  maps: InsightsSearchBundle["maps"];
  vaultDocuments: InsightsSearchBundle["vaultDocuments"];
  workspaces: InsightsSearchBundle["workspaces"];
  workspaceDocuments: InsightsSearchBundle["workspaceDocuments"];
  projects: InsightsSearchBundle["projects"];
  companies: InsightsSearchBundle["companies"];
  contacts: InsightsSearchBundle["contacts"];
  notes: InsightsSearchBundle["notes"];
  meetings: InsightsSearchBundle["meetings"];
  commands: InsightsSearchBundle["commands"];
}): InsightsSearchBundle {
  return {
    nodes: input.canIdeation ? input.nodes : [],
    maps: input.canIdeation ? input.maps : [],
    vaultDocuments: input.serverOk && input.canDocuments ? input.vaultDocuments : [],
    workspaces: input.workspaces,
    workspaceDocuments: input.workspaceDocuments,
    projects: input.canProjects ? input.projects : [],
    companies: input.serverOk && input.canCompanies ? input.companies : [],
    contacts: input.serverOk && input.canContacts ? input.contacts : [],
    notes: input.serverOk && input.canNotes ? input.notes : [],
    meetings: input.serverOk && input.canMeetings ? input.meetings : [],
    commands: input.enabled ? input.commands : [],
  };
}

/* eslint-disable-next-line complexity -- gates each authorised source independently */
export function useInsightsSearch(
  rawQuery: string,
  options: { enabled: boolean },
): {
  results: InsightsSearchResult[];
  grouped: InsightsSearchGroupView[];
  canAskInsights: boolean;
  isFetching: boolean;
} {
  const role = useCurrentRole();
  const flagsQuery = useFeatureFlags();
  const live = rawQuery.trim();
  const enabled = options.enabled && live.length > 0;
  const serverQuery = live.length >= SERVER_MIN ? live : "";
  const debouncedServer = useDebouncedValue(serverQuery, DEBOUNCE_MS);
  const serverAligned =
    enabled && debouncedServer === serverQuery && serverQuery.length >= SERVER_MIN;

  const canIdeation = roleCanAccess("ideation", role);
  const canDocuments = roleCanAccess("documents", role);
  const canContacts = roleCanAccess("contacts", role);
  const canCompanies = roleCanAccess("companies", role);
  const canProjects = roleCanAccess("projects", role);
  const canNotes = roleCanAccess("notes", role);
  const canMeetings = roleCanAccess("meetings", role);
  const canAi = roleCanAccess("ai", role);

  const nodesQuery = useIdeationMapNodes({ enabled: enabled && canIdeation });
  const mapsQuery = useIdeationMaps({ enabled: enabled && canIdeation });
  const workspacesQuery = useWorkspaces({ enabled });
  const workspaceDocs = useWorkspaceDocumentsIndex(enabled);
  const projectsQuery = useProjects(undefined, { enabled: enabled && canProjects });
  const documentsQuery = useDocuments(
    { limit: 8, search: debouncedServer || undefined },
    undefined,
    { enabled: serverAligned && canDocuments },
  );
  const companiesQuery = useCompanies(
    { search: debouncedServer || undefined, limit: 8 },
    { enabled: serverAligned && canCompanies },
  );
  const contactsQuery = useContacts(
    { search: debouncedServer || undefined, limit: 8 },
    { enabled: serverAligned && canContacts },
  );
  const notesQuery = useNotes(
    { search: debouncedServer || undefined, limit: 8 },
    { enabled: serverAligned && canNotes },
  );
  const meetingsQuery = useMeetingRecordings(
    { search: debouncedServer || undefined, limit: 8 },
    { enabled: serverAligned && canMeetings },
  );

  const enabledFlags = useMemo(() => {
    const set = new Set<string>();
    for (const flag of flagsQuery.data?.flags ?? []) {
      if (flag.enabled) set.add(flag.key);
    }
    return set;
  }, [flagsQuery.data]);

  const commands = useMemo(
    () =>
      filterNavSectionsForFlags(filterNavSectionsForRole(role), enabledFlags).flatMap((section) =>
        section.items.map((item) => ({
          id: item.id,
          label: item.label,
          href: item.href,
          keywords: item.keywords,
        })),
      ),
    [role, enabledFlags],
  );

  const insightsAiOn =
    flagsQuery.data?.flags.find((flag) => flag.key === "insights_ai")?.enabled === true;

  const bundle = assembleInsightsSearchBundle({
    enabled,
    serverOk: serverAligned,
    canIdeation,
    canDocuments,
    canProjects,
    canCompanies,
    canContacts,
    canNotes,
    canMeetings,
    nodes: nodesQuery.data?.nodes ?? [],
    maps: mapsQuery.data?.maps ?? [],
    vaultDocuments: documentsQuery.data?.documents ?? [],
    workspaces: workspacesQuery.data?.workspaces ?? [],
    workspaceDocuments: workspaceDocs.documents,
    projects: projectsQuery.data ?? [],
    companies: companiesQuery.data?.companies ?? [],
    contacts: (contactsQuery.data?.contacts ?? []).map((contact) => ({
      id: contact.id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      honorific: contact.honorific,
    })),
    notes: notesQuery.data?.notes ?? [],
    meetings: meetingsQuery.data?.recordings ?? [],
    commands,
  });

  const results = useMemo(
    () => (enabled ? buildInsightsSearchResults(bundle, live) : []),
    [bundle, enabled, live],
  );
  const grouped = useMemo(() => groupInsightsResults(results), [results]);

  const isFetching =
    enabled &&
    ((canIdeation && (nodesQuery.isFetching || mapsQuery.isFetching)) ||
      workspacesQuery.isFetching ||
      workspaceDocs.isFetching ||
      (canProjects && projectsQuery.isFetching) ||
      (serverAligned &&
        ((canDocuments && documentsQuery.isFetching) ||
          (canCompanies && companiesQuery.isFetching) ||
          (canContacts && contactsQuery.isFetching) ||
          (canNotes && notesQuery.isFetching) ||
          (canMeetings && meetingsQuery.isFetching))));

  return {
    results,
    grouped,
    canAskInsights: Boolean(enabled && insightsAiOn && canAi),
    isFetching,
  };
}
